var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { v4 } from 'uuid';
import { datadogLogs } from '@datadog/browser-logs';
import { postParentWindowMessage } from '../../../utils/post-message';
import sleep from '../../../utils/sleep';
import { getAnalyticsResponseEvent, getXmResponseEvent, } from '../../../utils/window-listeners';
/**
 * Stringifies a function
 * @param fn - Function
 * @returns - Stringified function
 */
export var stringifyFunction = function (fn) { return "(".concat(String(fn), ")()"); };
/**
 * Executes JS in Webview
 * @param fn - Function to execute
 * @param description - description for tracking in logs
 * @returns T
 */
export function execJsInSdk(fn, description) {
    var id = v4();
    var resolveFinal = function () { };
    var rejectFinal = function () { };
    var waitForResolve = function () {
        return new Promise(function (resolve, reject) {
            resolveFinal = resolve;
            rejectFinal = reject;
        });
    };
    function handlePostMessage(event) {
        var data = getXmResponseEvent(event);
        if (!data)
            return;
        datadogLogs.logger.info("client_integration_xm_response (".concat(description, ")"), {
            xmResponse: { data: data, id: id, description: description },
        });
        var payload = data.payload;
        if (payload.id !== id)
            return;
        if (payload.data.error)
            rejectFinal(payload.data.error);
        else
            resolveFinal(payload.data.result);
    }
    window.addEventListener('message', handlePostMessage);
    function postMessage() {
        return __awaiter(this, void 0, void 0, function () {
            var fnString, result;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        fnString = stringifyFunction(fn);
                        datadogLogs.logger.info("client_integration_xm_request (".concat(description, ")"), {
                            xmRequest: { fnString: fnString },
                        });
                        postParentWindowMessage({
                            type: 'PINWHEEL_INTERNAL_COMM_UP',
                            eventName: 'xm_request',
                            payload: {
                                id: id,
                                xm: fnString,
                            },
                        });
                        return [4 /*yield*/, waitForResolve()];
                    case 1:
                        result = _a.sent();
                        window.removeEventListener('message', handlePostMessage);
                        return [2 /*return*/, result];
                }
            });
        });
    }
    return postMessage();
}
/**
 * Allows Link to listen to input field updates in Webview.
 * Assumes `_pwcx` has been initialized (with `_initializePinwheelContext`).
 *
 * @param selector - selector of input field
 * @param key - key for value in `_pwcx` in Webview
 * @returns - void
 */
export function setUpContextListener(selector, key) {
    var fn = "\n    document.querySelector(\"".concat(selector, "\")?.addEventListener('input', (e) => {\n      window['setPwcx'](\"").concat(key, "\", e.target.value);\n    });\n  ");
    return execJsInSdk(new Function(fn), 'setUpContextListener'); // eslint-disable-line no-new-func
}
/**
 * Log "client_integration_error"
 * @param context - context object
 */
export function logError(context) {
    datadogLogs.logger.error('client_integration_error', {
        client_integration_error: context,
    });
}
/**
 * Wait for selector in webview
 * @param selector - selector to wait for
 */
export function waitForSelector(selector) {
    return __awaiter(this, void 0, void 0, function () {
        var fn;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    fn = "!document.querySelector(\"".concat(selector, "\")");
                    _a.label = 1;
                case 1: return [4 /*yield*/, execJsInSdk(new Function(fn))];
                case 2:
                    if (!_a.sent()) return [3 /*break*/, 4];
                    return [4 /*yield*/, sleep(1)];
                case 3:
                    _a.sent(); // eslint-disable-line no-new-func
                    return [3 /*break*/, 1];
                case 4: return [2 /*return*/];
            }
        });
    });
}
/**
 * Get window location of Edge Webview
 * @returns Promise<boolean>
 */
export function doesXPathExist(xpath) {
    xpath = xpath.replace(/"/g, '\\"');
    var fn = "const result = document.evaluate(\"".concat(xpath, "\", document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null);\n  return result.singleNodeValue !== null;");
    return execJsInSdk(new Function(fn)); // eslint-disable-line no-new-func
}
/**
 * Get window location of Edge Webview
 * @returns Promise<string>
 */
export function getEdgeCurrentLocation() {
    return execJsInSdk(function () { return window.location.href; });
}
/**
 * Get session storage item inside Edge Webview
 * @returns Promise<string>
 */
export function getEdgeSessionStorageItem(key) {
    var fn = "\n    const item = window.sessionStorage.getItem(\"".concat(key, "\");\n    return item !== null ? item : '';");
    return execJsInSdk(new Function(fn)); // eslint-disable-line no-new-func
}
/**
 * Get local storage item inside Edge Webview
 * @returns Promise<string>
 */
export function getEdgeLocalStorageItem(key) {
    var fn = "\n    const item = window.localStorage.getItem(\"".concat(key, "\");\n    return item !== null ? item : '';");
    return execJsInSdk(new Function(fn)); // eslint-disable-line no-new-func
}
/**
 * Returns b64 HTML of Page
 * @returns Promise<string>
 */
export function getEdgeB64Html() {
    return execJsInSdk(function () {
        var html = document.documentElement.outerHTML;
        return btoa(unescape(encodeURIComponent(html)));
    });
}
/**
 * Retrieves a Screenshot in B64 from Edge Webview
 * @returns T
 */
export function getScreenshotFromSdk() {
    var resolveFinal = function () { };
    var rejectFinal = function () { };
    var waitForResolve = function () {
        return new Promise(function (resolve, reject) {
            resolveFinal = resolve;
            rejectFinal = reject;
        });
    };
    function handleAnalyticsPostMessage(event) {
        var data = getAnalyticsResponseEvent(event);
        if (!data)
            return;
        datadogLogs.logger.info("client_integration_analytics_response", {
            analyticsResponse: { data: data },
        });
        var payload = data.payload;
        if (payload.data.error)
            rejectFinal(payload.data.error);
        else
            resolveFinal(payload.data.result);
    }
    window.addEventListener('message', handleAnalyticsPostMessage);
    function postMessage() {
        return __awaiter(this, void 0, void 0, function () {
            var result;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        datadogLogs.logger.info("client_integration_analytics_request", {});
                        postParentWindowMessage({
                            type: 'PINWHEEL_INTERNAL_COMM_UP',
                            eventName: 'analytics_request',
                        });
                        return [4 /*yield*/, waitForResolve()];
                    case 1:
                        result = _a.sent();
                        window.removeEventListener('message', handleAnalyticsPostMessage);
                        return [2 /*return*/, result];
                }
            });
        });
    }
    return postMessage();
}
